import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Toaster, toast } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import { format } from "date-fns";
import { BASE_URL } from "../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSyncAlt,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
} from "@fortawesome/free-solid-svg-icons";

const CardFeeProcessedHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    member_id: "",
    member_name: "",
    corporate_name: "",
    date_requested: "",
    date_uploaded: "",
    status: "",
    processed_at: "",
    corp_code: "",
    corp_prefix: "",
    member_type: "",
    renewal_date: "",
    renewal_type: "",
  });
  const [tempFilters, setTempFilters] = useState(filters);
  const [sortConfig, setSortConfig] = useState({
    key: "processed_at",
    direction: "desc",
  });

  const statusMap = {
    success: { name: "Success", color: "bg-green-100 text-green-800" },
    failed: { name: "Failed", color: "bg-red-100 text-red-800" },
  };

  const renewalTypeOptions = [
    { value: "", label: "ALL" },
    { value: "New Enrollment", label: "New Enrollment" },
    { value: "Renewal", label: "Renewal" },
  ];

  const columns = [
    { key: "corp_code", label: "Corp Code", sortable: true },
    { key: "corp_prefix", label: "Corp Prefix", sortable: true },
    { key: "member_type", label: "Member Type", sortable: true },
    { key: "renewal_date", label: "Renewal Date", sortable: true },
    { key: "renewal_type", label: "Renewal Type", sortable: true },
    { key: "member_id", label: "Member ID", sortable: true },
    { key: "member_name", label: "Member Name", sortable: true },
    { key: "corporate_name", label: "Corporate Name", sortable: true },
    { key: "date_requested", label: "Date Requested", sortable: true },
    { key: "date_uploaded", label: "Date Uploaded", sortable: true },
    { key: "status", label: "Status", sortable: true },
    { key: "error_message", label: "Error Message", sortable: false },
    { key: "processed_at", label: "Processed At", sortable: true },
  ];

  const fetchProcessedHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/card-replacement-fee/processed`,
        {
          params: {
            page: currentPage,
            per_page: itemsPerPage,
            search: searchTerm,
            sort_by: sortConfig.key,
            sort_direction: sortConfig.direction,
            ...filters,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        }
      );
      setTransactions(response.data.data.data);
      setTotalPages(response.data.data.last_page);
    } catch (error) {
      if (error.response?.status === 401) window.location.href = "/login";
      if (error.response?.status === 403) {
        toast.error("You do not have permission to access this.", {
          duration: 4000,
        });
      }
      if (error.response?.status === 429) {
        toast.error("Rate limit exceeded. Please try again later.", {
          duration: 4000,
        });
      }
      toast.error("Failed to fetch processed history.", { duration: 4000 });
    } finally {
      setLoading(false);
    }
  }, [filters, sortConfig, currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    fetchProcessedHistory();
  }, [fetchProcessedHistory]);

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc",
    });
  };

  const handleFilterChange = (key, value) => {
    setTempFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const renderPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (currentPage <= 3) endPage = Math.min(5, totalPages);
    else if (currentPage >= totalPages - 2)
      startPage = Math.max(totalPages - 4, 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-2 py-1 text-sm rounded-md transition-all duration-200 ${
            currentPage === i
              ? "bg-indigo-600 text-white"
              : "text-gray-700 hover:bg-indigo-100"
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const LoadingSkeleton = ({ rows = 5, columns = 13 }) => (
    <>
      {Array(rows)
        .fill()
        .map((_, i) => (
          <tr key={i} className="animate-pulse border-b border-gray-100">
            {Array(columns)
              .fill()
              .map((_, j) => (
                <td key={j} className="p-2">
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                </td>
              ))}
          </tr>
        ))}
    </>
  );

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "yyyy-MM-dd HH:mm:ss");
    } catch {
      return "-";
    }
  };

  const displayMemberType = (memberType) => {
    const type = memberType?.toLowerCase(); // convert to lowercase safely
    return type === "p" ? "Principal" : "Dependent";
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <div className="max-w-1xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-white rounded-lg shadow-sm">
            <FontAwesomeIcon icon={faSyncAlt} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Card Replacement Fee Processed List
            </h1>
            <p className="text-xs text-gray-500">
              View processed card fee transactions
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mx-2">
          {/* Top Controls */}
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between gap-3">
              <div className="relative flex-1 max-w-md">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                  aria-label="Search transactions"
                />
              </div>
              <button
                onClick={fetchProcessedHistory}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-all shadow-sm"
                data-tooltip-id="refresh-tooltip"
                data-tooltip-content="Refresh data"
              >
                <FontAwesomeIcon icon={faSyncAlt} />
                Refresh
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="p-3 bg-gray-50 border-b border-gray-100 overflow-x-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 min-w-max">
              {columns.map(
                (col) =>
                  col.key !== "error_message" && (
                    <div key={col.key} className="min-w-[150px]">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {col.label}
                      </label>
                      {col.key === "status" ? (
                        <select
                          value={tempFilters[col.key]}
                          onChange={(e) =>
                            handleFilterChange(col.key, e.target.value)
                          }
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                          aria-label="Filter by status"
                        >
                          <option value="">All Statuses</option>
                          {Object.entries(statusMap).map(([key, { name }]) => (
                            <option key={key} value={key}>
                              {name}
                            </option>
                          ))}
                        </select>
                      ) : col.key === "renewal_type" ? (
                        <select
                          value={tempFilters[col.key]}
                          onChange={(e) =>
                            handleFilterChange(col.key, e.target.value)
                          }
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                          aria-label="Filter by renewal type"
                        >
                          {renewalTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : [
                          "date_requested",
                          "date_uploaded",
                          "processed_at",
                          "renewal_date",
                        ].includes(col.key) ? (
                        <input
                          type="date"
                          value={tempFilters[col.key]}
                          onChange={(e) =>
                            handleFilterChange(col.key, e.target.value)
                          }
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                          aria-label={`Filter by ${col.label}`}
                        />
                      ) : (
                        <input
                          type="text"
                          placeholder={col.label}
                          value={tempFilters[col.key]}
                          onChange={(e) =>
                            handleFilterChange(col.key, e.target.value)
                          }
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                          aria-label={`Filter by ${col.label}`}
                        />
                      )}
                    </div>
                  )
              )}
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={applyFilters}
                className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-all shadow-sm"
              >
                Apply Filters
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full table-auto text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`p-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                        column.sortable ? "cursor-pointer hover:bg-gray-100" : ""
                      } ${
                        ["member_id", "member_name", "corporate_name"].includes(
                          column.key
                        )
                          ? "min-w-[120px]"
                          : ""
                      }`}
                      onClick={() => column.sortable && handleSort(column.key)}
                      aria-sort={
                        sortConfig.key === column.key
                          ? sortConfig.direction === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                    >
                      <div className="flex items-center gap-1">
                        {column.label}
                        {column.sortable && (
                          <div className="flex flex-col text-xs">
                            <span
                              className={`${
                                sortConfig.key === column.key &&
                                sortConfig.direction === "asc"
                                  ? "text-indigo-600"
                                  : "text-gray-400"
                              }`}
                            >
                              ▲
                            </span>
                            <span
                              className={`${
                                sortConfig.key === column.key &&
                                sortConfig.direction === "desc"
                                  ? "text-indigo-600"
                                  : "text-gray-400"
                              }`}
                            >
                              ▼
                            </span>
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <LoadingSkeleton
                    rows={itemsPerPage}
                    columns={columns.length}
                  />
                ) : !transactions.length ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="p-2 text-center text-gray-500 text-xs"
                    >
                      No transactions found.
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction) => (
                    <tr
                      key={transaction.id}
                      className="border-b hover:bg-gray-50"
                    >
                      <td className="p-2 text-xs">
                        {transaction.corp_code || "-"}
                      </td>
                      <td className="p-2 text-xs">
                        {transaction.corp_prefix || "-"}
                      </td>
                      <td className="p-2 text-xs">
                        {displayMemberType(transaction.member_type)}
                      </td>
                      <td className="p-2 text-xs">
                        {transaction.renewal_date || "-"}
                      </td>
                      <td className="p-2 text-xs">
                        {transaction.renewal_type || "-"}
                      </td>
                      <td
                        className="p-2 text-xs min-w-[120px] truncate"
                        data-tooltip-id={`member-id-${transaction.id}`}
                        data-tooltip-content={transaction.member_id}
                      >
                        {transaction.member_id}
                      </td>
                      <td
                        className="p-2 text-xs min-w-[120px] truncate"
                        data-tooltip-id={`member-name-${transaction.id}`}
                        data-tooltip-content={transaction.member_name}
                      >
                        {transaction.member_name}
                      </td>
                      <td
                        className="p-2 text-xs min-w-[120px] truncate"
                        data-tooltip-id={`corporate-name-${transaction.id}`}
                        data-tooltip-content={transaction.corporate_name}
                      >
                        {transaction.corporate_name}
                      </td>
                      <td className="p-2 text-xs">
                        {transaction.date_requested || "-"}
                      </td>
                      <td className="p-2 text-xs">
                        {transaction.date_uploaded || "-"}
                      </td>
                      <td className="p-2">
                        <span
                          className={`px-1.5 py-0.5 rounded text-xs ${
                            statusMap[transaction.status]?.color ||
                            "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {statusMap[transaction.status]?.name || "Unknown"}
                        </span>
                      </td>
                      <td
                        className="p-2 text-xs truncate"
                        data-tooltip-id={`error-message-${transaction.id}`}
                        data-tooltip-content={transaction.error_message || "-"}
                      >
                        {transaction.error_message || "-"}
                      </td>
                      <td className="p-2 text-xs">
                        {formatDate(transaction.processed_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-t border-gray-100">
            <div className="mb-2 sm:mb-0">
              <select
                value={itemsPerPage}
                onChange={(e) =>
                  handleItemsPerPageChange(Number(e.target.value))
                }
                className="px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                aria-label="Items per page"
              >
                {[5, 10, 20, 50, 100].map((val) => (
                  <option key={val} value={val}>
                    {val} per page
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || loading}
                className="px-2 py-1 text-sm text-gray-700 rounded-md hover:bg-indigo-100 disabled:opacity-50 transition-all"
                aria-label="First page"
              >
                First
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="px-2 py-1 text-sm text-gray-700 rounded-md hover:bg-indigo-100 disabled:opacity-50 transition-all"
                aria-label="Previous page"
              >
                <FontAwesomeIcon icon={faAngleLeft} /> Previous
              </button>
              {renderPageNumbers()}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="px-2 py-1 text-sm text-gray-700 rounded-md hover:bg-indigo-100 disabled:opacity-50 transition-all"
                aria-label="Next page"
              >
                Next <FontAwesomeIcon icon={faAngleRight} />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || loading}
                className="px-2 py-1 text-sm text-gray-700 rounded-md hover:bg-indigo-100 disabled:opacity-50 transition-all"
                aria-label="Last page"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      </div>
      <Tooltip id="refresh-tooltip" place="top" />
      {transactions.map((transaction) => (
        <React.Fragment key={transaction.id}>
          <Tooltip id={`member-id-${transaction.id}`} place="top" />
          <Tooltip id={`member-name-${transaction.id}`} place="top" />
          <Tooltip id={`corporate-name-${transaction.id}`} place="top" />
          <Tooltip id={`error-message-${transaction.id}`} place="top" />
        </React.Fragment>
      ))}
    </div>
  );
};

export default CardFeeProcessedHistory;